<template>

  <div class="scrollable only-y" style="width: 100%;" v-loading="dashboardLoading">

<!--    This is the layout create / edit form-->
    <LayoutForm
      :context="layoutFormContext"
      :layout-data="layoutFormData"
      :visible="layoutFormVisible"
      :queue-options="queueOptions"
      @close="closeLayoutForm"
      @create="createLayout"
      @save="editLayout"
    />

<!--    This is the Grid-->
    <GridLayout
      :layout.sync="layout"
      :col-num="colNum"
      :row-height="35"
      :is-draggable="true"
      :is-resizable="true"
      :vertical-compact="true"
      :use-css-transforms="true"
    >

      <GridItem
        v-for="(item, index) in layout"
        :key="index"
        :static="false"
        :x="item.x"
        :y="item.y"
        :w="item.w"
        :h="item.h"
        :i="item.i"
      >
        <component
          :is="widgetComponent(item.type)"
          v-bind="{
           widgetIndex: item.i,
           title: item.title,
           filterQueueIds: item.filterQueueIds,
           queueNameKey: queueNameKey,
           columnFields: item.columnFields || [],
          }"
          @nameUpdate="updateWidgetName"
          @filterQueuesUpdate="updateWidgetFilterQueues"
          @removeWidget="removeFromLayout"
          @updateActiveColumns="updateTableColumns"
        />
      </GridItem>
    </GridLayout>

    <div v-if="!userLayouts.length">
      <h2 class="mt-0">
        Get started by using the toolbar to create your first layout
      </h2>
    </div>

    <div v-if="userLayouts.length && !layout.length">
      <h2 class="mt-0">
        Add your first widget using the 'Add Widget' button.
      </h2>
    </div>

  </div>

</template>

<script>

import {globalEventBus} from "@/globalEventBus";
import { mapGetters} from "vuex";
import feathersClient from "@/feathers/feathers-client";
import { debounce } from 'lodash';
import { GridLayout, GridItem } from 'vue-grid-layout';
import DadsSelect from "@/components/select";
import LayoutForm from './components/layout-form';
import moment from 'moment-timezone';

//dynamic components
const inboundAnswered = () => import('./widget-layouts/inbound-answered');
const abandonedCalls = () => import('./widget-layouts/abandoned-calls');
const agentTable = () => import ('./widget-layouts/agent-table');
const queuesTable = () => import('./widget-layouts/queues-table');
const queuedCallbacks = () => import('./widget-layouts/queued-callbacks');
const completedCallbacks = () => import('./widget-layouts/complete-callbacks');
const outboundCalls = () => import('./widget-layouts/outbound-calls');
const totalInbound = () => import('./widget-layouts/inbound-calls');
const avgAnswerTime = () => import('./widget-layouts/avg-answer-time');
const transferredCalls = () => import('./widget-layouts/transferred-calls');
const exitStats = () => import('./widget-layouts/exit-stats');
const activeCalls = () => import('./widget-layouts/active-calls');
const waitingCalls = () => import('./widget-layouts/waiting-calls');
const waitingCallList = () => import('./widget-layouts/waiting-call-list');
const activeCallList = () => import('./widget-layouts/active-call-list');
const agentTableAggregated = () => import('./widget-layouts/agent-table-aggregated');

export default {
  name: 'QueueManagerDashboard',
  components: { GridLayout, GridItem, DadsSelect, LayoutForm },
  data() {
    return {
      pageLoadTime: moment().format('YYYY-MM-DD'),
      resetPoller: null,
      durationPoller: null,
      durationUpdateInterval: 3,
      pausePoller: null,
      pauseUpdateInterval: 5,
      updatePauseTimeout: null,
      statResetComplete: false,
      dashboardLoading: false,
      enableAutoSave: true,
      queueIds: [],
      userLayouts: [],
      selectedLayout: null,
      layout: [],
      queueOptions: [],
      queueNameKey: {},
      layoutFormVisible: false,
      layoutFormContext: 'create',
      layoutFormData: null,
      maxWidgetIndex: 0,
      colNum: 24,
      channelsJoined: false,
      tenantChannelJoined: false,

      widgetOptions: [
        {name: 'Abandoned', command: 'abandonedCalls', sprite: 'call-inbound', color: 'voxo-red'},
        {name: 'Active calls', command: 'activeCalls', sprite: 'receptionist', color: 'voxo-blue'},
        {name: 'Agents By Queue', command: 'agentTable', sprite: 'user', color: 'voxo-blue'},
        {name: 'Agent Totals', command: 'agentTableAggregated', sprite: 'user', color: 'voxo-pink'},
        {name: 'Agent outbound', command: 'outboundCalls', sprite: 'call-outbound', color: 'voxo-purple'},
        {name: 'Avg answer time', command: 'avgAnswerTime', sprite: 'clock', color: 'voxo-green'},
        {name: 'Complete callbacks', command: 'completedCallbacks', sprite: 'call-outbound', color: 'voxo-aqua'},
        {name: 'Exits', command: 'exitStats', sprite:'exit', color: 'voxo-orange' },
        {name: 'Inbound answered', command: 'inboundAnswered', sprite: 'call-inbound', color: 'voxo-green'},
        {name: 'Queued callbacks', command: 'queuedCallbacks', sprite: 'refresh', color: 'voxo-orange'},
        {name: 'Queues', command: 'queuesTable', sprite: 'queue', color: 'voxo-red'},
        {name: 'Total inbound', command: 'totalInbound', sprite: 'call-inbound', color: 'voxo-blue'},
        {name: 'Transfers', command: 'transferredCalls', sprite: 'transfer', color: 'voxo-orange'},
        {name: 'Waiting calls', command: 'waitingCalls', sprite: 'call-inbound', color: 'voxo-yellow'},
        {name: 'Waiting call list', command: 'waitingCallList', sprite: 'clock', color: 'voxo-yellow'},
        {name: 'Active call list', command: 'activeCallList', sprite: 'queue', color: 'voxo-green'}
      ]
    }
  },
  computed: {
    ...mapGetters('queueDashQueues', {queueStoreData: 'list', queueStoreItem: 'get'}),
    ...mapGetters('queueDashAgents', {agentStoreData: 'list', agentStoreItem: 'get', agentStoreList: 'find'}),
    ...mapGetters('queueDashAgentAggregates', {agentAggregateData: 'list', aggregateAgentItem: 'get', aggregateAgentList: 'find'}),
    ...mapGetters('queueDashWaiting', {waitingCallData: 'list'}),
    ...mapGetters('queueDashActive', {activeCallData: 'list'}),
    user() {
      return this.$store.state.auth.user;
    },
    selectedTenant () {
      return this.$store.state.selectedTenant;
    },
    lastSelectedLayout () {
      return this.$store.state.lastManagerDashboardLayout;
    },
    selectedQueueLayout() {
      return this.$store.state.selectedQueueLayout;
    },
    userIsQueueManager() {
      if(this.user){
        return +this.user.userRole === 7;
      }
      return false;
    }
  },
  watch: {

    //updates the aggregates when data about a queue changes
    queueStoreData () {
      const numQueues = this.queueStoreData.length;

      const aggregates = {
        totalInbound: 0,
        inboundComplete: 0,
        totalAbandoned: 0,
        queuedCallbacks: 0,
        completedCallbacks: 0,
        successfulCallbacks: 0,
        failedCallbacks: 0,
        totalOutbound: 0,
        answeredOutbound: 0,
        noAnswerOutbound: 0,
        avgTimeToAnswer: 0,
        totalTransfers: 0,
        attendedTransfers: 0,
        blindTransfers: 0,
        totalExits: 0,
        exitKey: 0,
        exitTimeout: 0,
        abandonRate: 0,
        avgAbandonWaitTime: 0,
        waitTimeAbandoned: 0,
        waitTimeAnswered: 0,
        activeCalls: 0,
        waitingCalls: 0,
        refused: 0,
        longestCurrentHold: null,
        longestTalkTime: null
      };

      if(numQueues){
        for(let i = 0; i < numQueues; i++) {
          const { inboundComplete, totalInbound, abandoned, queuedCallbacks, completedCallbacks, successfulCallbacks,
            failedCallbacks, totalOutbound, outboundAnswered, outboundNoAnswer, totalTransfers,
            attendedTransfers, blindTransfers, totalExits, exitKey, exitTimeout, waitTimeAbandoned, waitTimeAnswered, refused,
            activeCalls, waitingCalls, longestCurrentHold, longestTalkTime} = this.queueStoreData[i];

          aggregates.inboundComplete += inboundComplete;
          aggregates.totalInbound += totalInbound;
          aggregates.totalAbandoned += abandoned;
          aggregates.queuedCallbacks += queuedCallbacks;
          aggregates.completedCallbacks += completedCallbacks;
          aggregates.successfulCallbacks += successfulCallbacks;
          aggregates.failedCallbacks += failedCallbacks;
          aggregates.totalOutbound += totalOutbound;
          aggregates.answeredOutbound += +outboundAnswered;
          aggregates.noAnswerOutbound += +outboundNoAnswer;
          aggregates.totalTransfers += +totalTransfers;
          aggregates.attendedTransfers += +attendedTransfers;
          aggregates.blindTransfers += +blindTransfers;
          aggregates.totalExits += +totalExits;
          aggregates.exitKey += +exitKey;
          aggregates.exitTimeout += +exitTimeout;
          aggregates.waitTimeAbandoned += +waitTimeAbandoned;
          aggregates.waitTimeAnswered += +waitTimeAnswered;
          aggregates.refused += +refused;
          aggregates.waitingCalls += +waitingCalls;
          aggregates.activeCalls += +activeCalls;

          if(longestCurrentHold !== null){
            aggregates.longestCurrentHold = longestCurrentHold > aggregates.longestCurrentHold
              ? longestCurrentHold
              : aggregates.longestCurrentHold
          }

          if(longestTalkTime !== null){
            aggregates.longestTalkTime = longestTalkTime > aggregates.longestTalkTime
              ? longestTalkTime
              : aggregates.longestTalkTime
          }
        }
      }

      //calculate averages
      if(aggregates.inboundComplete){
        aggregates.avgTimeToAnswer = +(aggregates.waitTimeAnswered / aggregates.inboundComplete).toFixed(0);
      }

      if(aggregates.totalAbandoned) {
        aggregates.abandonRate = +(aggregates.totalAbandoned / aggregates.totalInbound * 100).toFixed(0);
        aggregates.avgAbandonWaitTime = +(aggregates.waitTimeAbandoned / aggregates.totalAbandoned).toFixed(0);
      }

      this.$store.commit('setQueueManagerAggregates', aggregates);
    },

    //watches changes to layout so we can trigger an autosave to the db
    layout: {
      handler: debounce(function () {
        if(this.enableAutoSave){
          //console.log('layout change was made so we can trigger a save here');
          this.saveLayout();
        }
      }, 3000),
      deep: true
    },

  },
  methods: {

    onLayoutCommand(command) {

      if(command === 'edit'){
        this.loadEditLayoutForm();
        return;
      }

      if(command === 'remove'){
        this.removeLayout()
        return;
      }

      if(command === 'create'){
        this.loadCreateLayoutForm()
      }

    },

    onAddWidgetCommand(command) {
      //console.log('adding new widget of type', command);
      this.widgetBuilder(command);
    },

    //init function responsible for getting queues the user has access to
    async initDashboard() {

      //load available queues
      try {

        const query = {
          tenantId: this.selectedTenant.id,
          $select: ['id', 'name']
        }

        if(this.userIsQueueManager){
          query.id = {$in: [...this.user.adminQueues]};
        }

        this.queueOptions = await feathersClient.service('queues').find({ query });

        let nameKey = {};
        for(let queue of this.queueOptions) {
          nameKey[queue.id] = queue.name;
        }
        this.queueNameKey = {...nameKey};

      } catch (e) {
        console.log('could not load queues', e);
      }

      //load dashboard
      await this.loadDashboard()

    },

    //does the work to render the selected layout.
    async loadDashboard(selectedLayout = null) {

      //dashboard load processing
      this.dashboardLoading = true;

      //disable watcher for auto save here
      this.enableAutoSave = false;

      //reset the current grid layout
      this.layout = [];

      //leave connected socket channels
      if(this.channelsJoined) {
        await this.leaveQueueChannels();
      }

      //get the layouts for the user
      if(!(this.userLayouts.length && selectedLayout)){
        await this.queryLayouts();
      }

      //set selected layout to null if no layouts available
      if(!this.userLayouts.length){
        this.selectedLayout = null;
        this.$store.commit('setLastManagerDashboardLayout', null);
        this.$store.commit('setSelectedQueueLayout', null);
      }

      if(this.userLayouts.length) {

       // console.log('layout load', selectedLayout);

        //get the selected layout for the user
        const { layout, queueIds } = await this.setSelectedLayout(selectedLayout ? selectedLayout.id : null);

        //set the current queue ids
        //if user is queue admin, double check the queues against loaded queues (allowed queues)
        this.queueIds = this.userIsQueueManager ? queueIds.filter(qId => this.user.adminQueues.includes(qId)) : queueIds;

        //return the params required for the query.
        const config = this.buildQueryConfig();

        //remove any existing store data
        this.$store.commit('queueDashQueues/clearList');
        this.$store.commit('queueDashAgents/clearList');
        this.$store.commit('queueDashWaiting/clearList');
        this.$store.commit('queueDashActive/clearList');
        this.$store.commit('queueDashAgentAggregates/clearList')
        clearInterval(this.durationPoller);
        clearInterval(this.pausePoller);
        this.durationPoller = null;
        this.pausePoller = null;

        //query data and load grid layout
        await this.queryData(config);
        this.layout = layout;

        //get waiting call count and active call count for the queues
        await this.updateCallCounts();
        this.updatePauses();

        //set up the duration poller that updates call durations on an interval
        this.durationPoller = setInterval( () => {
          this.updateDurations();
        }, this.durationUpdateInterval * 1000);

        //set up the pause poller that updates longest current pause
        this.pausePoller = setInterval(() => {
          this.updatePauses();
        }, this.pauseUpdateInterval * 1000);

        //join the real time event channels
        await Promise.all([
          this.joinQueueChannels(),
          this.joinTenantChannels()
        ]);

      }

      this.dashboardLoading = false;

      //enable watcher for auto save here
      setTimeout( () => {
        this.enableAutoSave = true;
      }, 3250);

    },

    //load the user's saved dashboard layouts
    async queryLayouts() {

      try {
        const layouts = await feathersClient.service('queue-layouts').find({
          query: {
            userId: this.user.id,
            tenantId:  this.selectedTenant.id,
            $select: ['id', 'name', 'queueIds']
          }
        });

     //   console.log('here are layouts', layouts);
        this.userLayouts = layouts;
        this.$store.commit('setUserQueueLayouts', [...this.userLayouts]);

      } catch (e) {
        console.log('layouts could not be loaded', e);
      }

    },

    async saveLayout() {

      try {
        await feathersClient.service('queue-layouts').patch(this.selectedLayout.id,
          {
            layout: [...this.layout]
          },
          {
            query: {
              userId: this.user.id
            }
          }
        );
      } catch (e) {
        console.log('could not save layout!', e)
      }

    },

    async setSelectedLayout(layoutId) {

      if(!layoutId) {

        //check persisted store for last selected layout
        if(this.lastSelectedLayout) {
          const existingLayout = this.userLayouts.find(item => item.id === this.lastSelectedLayout.id);
          this.selectedLayout = existingLayout ? existingLayout : this.userLayouts[0];
        } else {
          this.selectedLayout = this.userLayouts[0];
        }

      } else {
        this.selectedLayout = this.userLayouts.find(item => item.id === layoutId);
      }

      //persist selected layout to store
      this.$store.commit('setLastManagerDashboardLayout', {...this.selectedLayout});
      this.$store.commit('setSelectedQueueLayout', {...this.selectedLayout});

      const { id } = this.selectedLayout;

      //query the layout details here. this just guarantees data persistence
      let layout = null;
      try {
        layout = await feathersClient.service('queue-layouts').get(id, {
          query: {
            userId: this.user.id
          }
        });

        this.maxWidgetIndex = 0;
        for(let item of layout.layout) {
          this.maxWidgetIndex = item.i > this.maxWidgetIndex ? item.i : this.maxWidgetIndex;
        }

      } catch (e) {
        console.log('could not get layout details', e);
      }

      return layout;

    },

    buildQueryConfig() {
      return {
        command: 'managerDashboard',
        tenantId: this.selectedTenant.id,
        timezone: this.user.timezone,
        queueId: this.queueIds.join(',')
      }
    },

    //api call to grab all the current stats on layout load
    async queryData(config) {

      try {
        const payload = await feathersClient.service('queue-stats').find({
          query: config
        });

        const { stats, agentsTable, aggregateAgentsTable } = payload;

        this.$store.commit('queueDashQueues/addItems', stats);
        this.$store.commit('queueDashAgents/addItems', agentsTable);
        this.$store.commit('queueDashAgentAggregates/addItems', aggregateAgentsTable);

      } catch (e) {
        console.log('failed to load data');
      }
    },

    //api request to return active and waiting call counts for the queueIds
    //also load current call list of calls in line
    async updateCallCounts() {

      try {

        const payload = await feathersClient.service('nats-actions-handler').create({
          reqType: 'queueCallCounts',
          queueIds: [...this.queueIds]
        });

        let currentCallList = [];
        let activeCallList = [];
        for(let item of payload) {
          const {id: queueId, waiting, active, callList, longestCurrentHold, activeCallList: activeCalls, longestTalkTime} = item;
          let queue = {...this.queueStoreItem(queueId)};
          queue.activeCalls += active;
          queue.waitingCalls += waiting;
          queue.longestCurrentHold = longestCurrentHold;
          queue.longestTalkTime = longestTalkTime;
          this.$store.commit('queueDashQueues/updateItem', queue);
          currentCallList = [...currentCallList, ...callList];
          activeCallList = [...activeCallList, ...activeCalls];
        }

        this.$store.commit('queueDashWaiting/addItems', [...currentCallList]);
        this.$store.commit('queueDashActive/addItems', [...activeCallList]);

      } catch (e) {
        console.log('had trouble loading call counts', e);
      }

    },

    //updates the durations of calls in the waiting list
    updateDurations() {

      let currentCalls = [];
      let activeCalls = [];
      const countCalls = this.waitingCallData.length;
      const countActiveCalls = this.activeCallData.length;
      const queuesMaxWait = {};
      const queuesMaxDuration = {};
      for(let queue of this.queueIds) {
        queuesMaxWait[queue] = null;
        queuesMaxDuration[queue] = null;
      }

      for(let i = 0; i < countCalls; i++) {
        const call = {...this.waitingCallData[i]};
        call.holdTime = this.waitingCallData[i].holdTime + this.durationUpdateInterval;
        currentCalls.push(call)

        const { queueId, holdTime } = call;
        queuesMaxWait[queueId] = queuesMaxWait[queueId]
          ? holdTime > queuesMaxWait[queueId] ? holdTime : queuesMaxWait[queueId]
          : holdTime;
      }

      for(let i = 0; i < countActiveCalls; i++) {
        const call = {...this.activeCallData[i]};
        call.talkTime = call.talkTime + this.durationUpdateInterval;
        activeCalls.push(call);

        const { queueId, talkTime } = call;
        queuesMaxDuration[queueId] = queuesMaxDuration[queueId]
          ? talkTime > queuesMaxDuration[queueId] ? talkTime : queuesMaxDuration[queueId]
          : talkTime
      }

      const queueItems = [];
      for(let queueId in queuesMaxWait) {
        const queue = {...this.queueStoreItem(queueId)};
        queue.longestCurrentHold = queuesMaxWait[queueId];
        queue.longestTalkTime = queuesMaxDuration[queueId];
        queueItems.push(queue);
      }

      this.$store.commit('queueDashWaiting/updateItems', currentCalls);
      this.$store.commit('queueDashActive/updateItems', activeCalls);
      this.$store.commit('queueDashQueues/updateItems', queueItems);

      //update the longest talk durations in the agents table
      const activeCallList = this.activeCallData;
      const peerDurations = {};
      const updatedAggregateList = [];
      for(let i = 0; i < activeCallList.length; i++){

        const { connectedAgent, talkTime } = activeCallList[i];

        //find the peer name
        const agent = this.aggregateAgentList({ query: {name: connectedAgent} }).data;
        if(agent.length){

          const peerName = agent[0].peerName;
          peerDurations[peerName] = peerDurations[peerName]
            ? (talkTime < peerDurations[peerName] ? peerDurations[peerName] : talkTime)
            : talkTime;
        }

      }

      for(let i = 0; i < this.agentAggregateData.length; i++){
        const data = {...this.agentAggregateData[i]};
        if(typeof data === 'object'){
          data.longestCurrentTalkTime = peerDurations[data.peerName] ? peerDurations[data.peerName] : 0;
          updatedAggregateList.push({...data});
        }
      }

      this.$store.commit('queueDashAgentAggregates/updateItems', updatedAggregateList);



    },

    updatePauses() {

      //loop over queue by queuet set
      const peerPauses = {};
      const peerPauseReasons = {};
      for(let i = 0; i < this.agentStoreData.length; i++){
        const { peerName, paused, lastPauseUnix, lastPauseReason  } = this.agentStoreData[i];

        if(paused){
          const pauseTime = lastPauseUnix
            ? moment().unix() - lastPauseUnix
            : moment().unix() - moment().startOf('day').unix();

          peerPauses[peerName] = peerPauses[peerName]
            ? (pauseTime > peerPauses[peerName] ? pauseTime : peerPauses[peerName])
            : pauseTime

          peerPauseReasons[peerName] = peerPauseReasons[peerName]
            ? (pauseTime === peerPauses[peerName] ? lastPauseReason : peerPauseReasons[peerName])
            : lastPauseReason
        }

      }

      //loop over aggregated set and update
      const updatedAggregateList = [];
      for(let i = 0; i < this.agentAggregateData.length; i++){
        const data = {...this.agentAggregateData[i]};
        if(typeof data === 'object'){
          data.longestCurrentPauseTime = peerPauses[data.peerName] ? peerPauses[data.peerName] : 0;
          data.lastPauseReason = peerPauseReasons[data.peerName] ? peerPauseReasons[data.peerName] : '';
          data.paused = data.longestCurrentPauseTime ? 1 : 0;
          updatedAggregateList.push({...data});
        }
      }

      this.$store.commit('queueDashAgentAggregates/updateItems', updatedAggregateList);

    },

    //conditionally return the proper widget component
    widgetComponent(type) {

      const widgetMap = {
        inboundAnswered,
        abandonedCalls,
        agentTable,
        queuesTable,
        queuedCallbacks,
        completedCallbacks,
        outboundCalls,
        totalInbound,
        avgAnswerTime,
        transferredCalls,
        exitStats,
        activeCalls,
        waitingCalls,
        waitingCallList,
        activeCallList,
        agentTableAggregated
      };

      return widgetMap[type];

    },

    //this will result in an auto save
    updateWidgetName(data) {
      const { index, name } = data;
      const layoutIndex = this.layout.findIndex(item => item.i === index);
      if(layoutIndex >= 0) {
        const widget = {...this.layout[layoutIndex]};
        widget.title = name;
        this.layout.splice(layoutIndex, 1, widget);
      }

    },

    //this will result in an auto save
    updateWidgetFilterQueues(data) {
      const {index, filterQueueIds} = data;
      const layoutIndex = this.layout.findIndex(item => item.i === index);
      if(layoutIndex >= 0){
        const widget = {...this.layout[layoutIndex]};
        widget.filterQueueIds = [...filterQueueIds];
        this.layout.splice(layoutIndex, 1, widget);
      }
    },

    //this will trigger an autosave
    updateTableColumns(data) {
      const {index, columns} = data;
      const layoutIndex = this.layout.findIndex(item => item.i === index);
      if(layoutIndex >= 0){
        const widget = {...this.layout[layoutIndex]};
        widget.columnFields = [...columns];
        this.layout.splice(layoutIndex, 1, widget);
      }
    },

    //remove widget from the layout
    async removeFromLayout(index) {

      try {
        await this.$confirm('Remove widget from layout?');
      } catch (e) {
        return;
      }

      const layoutIndex = this.layout.findIndex(item => item.i === index);
      if(layoutIndex >= 0) {
        this.layout.splice(layoutIndex, 1);
      }
    },

    //load selected layout edit frm
    loadEditLayoutForm(){
      this.layoutFormContext = 'edit';
      this.layoutFormData = {...this.selectedLayout};
      this.layoutFormVisible = true;
    },

    //close out the layout form
    closeLayoutForm() {
      this.layoutFormContext = 'create';
      this.layoutFormData = null;
      this.layoutFormVisible = false;
    },

    //pop up new layout create form
    loadCreateLayoutForm() {
      this.layoutFormContext = 'create';
      this.layoutFormData = null;
      this.layoutFormVisible = true;
    },

    //modify the currently selected layout
    async editLayout(data) {

      const editBody = {
        ...data
      };

      try {
        const savedLayout = await feathersClient.service('queue-layouts').patch(this.selectedLayout.id, editBody, {
          query: {
            userId: this.user.id
          }
        });

        const layoutIndex = this.userLayouts.findIndex(item => item.id === savedLayout.id);
        if(layoutIndex >= 0){
          this.userLayouts.splice(layoutIndex, 1, savedLayout);
          this.$store.commit('setUserQueueLayouts', [...this.userLayouts]);
          await this.loadDashboard();
        }

      } catch (e) {
        console.log('could not save layout', e)
      }

    },

    //add a new layout to db and list
    async createLayout(data) {

      const createBody = {
        ...data,
        userId: this.user.id,
        tenantId: this.selectedTenant.id,
        layout: []
      }

      try {
        const newLayout = await feathersClient.service('queue-layouts').create(createBody, {
          query: {
            userId: this.user.id
          }
        });

        this.userLayouts = [...this.userLayouts, newLayout]
        this.$store.commit('setUserQueueLayouts', [...this.userLayouts]);
        await this.loadDashboard(newLayout);

      } catch (e) {
        console.log('could not create layout', e);
      }

    },

    //duplicate the currently selected layout
    async duplicateLayout() {

        const duplicateBody = {...this.selectedLayout};
        duplicateBody.name = `${duplicateBody.name} - Copy`;
        duplicateBody.layout = [...this.layout];
        duplicateBody.tenantId = this.selectedTenant.id;
        duplicateBody.userId = this.user.id;
        delete duplicateBody.id;

        try {
          await this.$confirm('Duplicate layout?');
        } catch (e) {
          return;
        }

        try {

          const newLayout = await feathersClient.service('queue-layouts').create(duplicateBody, {
            query: {
              userId: this.user.id
            }
          });

          this.userLayouts = [...this.userLayouts, newLayout]
          this.$store.commit('setUserQueueLayouts', [...this.userLayouts]);
          await this.loadDashboard(newLayout);

        } catch (e) {
          console.log('could not duplicate layout', e);
        }
    },

    //remove an entire layout from db and list
    async removeLayout() {

      try {
        await this.$confirm(`Remove ${this.selectedLayout.name} permanently?`);
      } catch (e) {
        console.log('remove canceled')
        return;
      }


      try {
        const removedLayout = await feathersClient.service('queue-layouts').remove(this.selectedLayout.id, {
          query: {
            userId: this.user.id
          }
        });

        const layoutIndex = this.userLayouts.findIndex(item => item.id === removedLayout.id);
        if(layoutIndex >= 0){
          this.userLayouts.splice(layoutIndex, 1);
          this.$store.commit('setUserQueueLayouts', [...this.userLayouts]);

          //remove from store because this was last selected
          this.$store.commit('setLastManagerDashboardLayout', null);

          //load the first layout in the list
          await this.loadDashboard();
        }
      } catch (e) {
        console.log('could not remove layout', e);
      }

    },

    //inserts a brand new widget with sane defaults
    widgetBuilder(type){

      let totalWidth = 0;
      for(let item of this.layout){
        totalWidth += item.w;
      }

      let columnFields = [];

      if (type === 'agentTable') {
        columnFields = ['queueId', 'loggedIn', 'paused', 'pauseControl', 'name', 'extension', 'inboundComplete',
          'blindTransfers', 'attendedTransfers', 'totalTransfers', 'inboundTalkTime', 'outboundAnswered',
          'outboundTalkTime', 'totalOutbound', 'refused', 'inboundDirect'];
      }

      if (type === 'queuesTable') {
        columnFields = ['queueId', 'inboundComplete', 'avgTimeToAnswer', 'attendedTransfers', 'blindTransfers', 'totalTransfers',
          'exitKey', 'exitTimeout', 'totalExits', 'abandoned', 'abandonRate', 'avgAbandonWaitTime', 'totalInbound', 'refused',
          'outboundAnswered', 'outboundNoAnswer', 'totalOutbound'];
      }

      if (type === 'agentTableAggregated') {
        columnFields = ['queuesCount', 'loggedIn', 'paused', 'pauseControl', 'longestCurrentPauseTime', 'longestCurrentTalkTime', 'name', 'extension', 'inboundComplete',
          'blindTransfers', 'attendedTransfers', 'totalTransfers', 'inboundTalkTime', 'outboundAnswered',
          'outboundTalkTime', 'totalOutbound', 'refused', 'inboundDirect'];
      }

      this.maxWidgetIndex++;
      const widget = {
        static: false,
        filterQueueIds: [...this.queueIds],
        i: !this.layout.length ? 1 : this.maxWidgetIndex,
        x: totalWidth % this.colNum,
        y: this.layout.length + this.colNum,
        w: 6,
        h: 8,
        type,
        columnFields
      };

      const titleMap = {
        inboundAnswered: 'INBOUND ANSWERED',
        queuedCallbacks: 'CALLBACKS QUEUED',
        completedCallbacks: 'CALLBACK COMPLETE',
        abandonedCalls: 'ABANDONED',
        totalInbound: 'INBOUND TOTAL',
        avgAnswerTime: 'AVG ANSWER TIME',
        transferredCalls: 'TRANSFERRED CALLS',
        exitStats: 'EXITS',
        agentTable: 'AGENTS BY QUEUE',
        agentTableAggregated: 'AGENT TOTALS',
        queuesTable: 'QUEUES',
        outboundCalls: 'AGENT OUTBOUNDS',
        activeCalls: 'ACTIVE CALLS',
        waitingCalls: 'WAITING CALLS',
        waitingCallList: 'WAITING CALL LIST',
        activeCallList: 'ACTIVE CALL LIST'
      }

      if(!titleMap[type]){
        return;
      }

      widget.title = titleMap[type];
      this.layout = [...this.layout, widget];

    },

    //joins proper queue event channels so we can update stats in real time
    async joinQueueChannels() {

      const requests = this.queueIds.map(queueId => {
        return feathersClient.service('channel-memberships').create({
          roomType: 'queues',
          queueId
        }).then(res => console.log(res));
      });

      try {
        await Promise.all(requests);
        this.channelsJoined = true;
      } catch (e) {
        console.log('could not join channels', e);
        this.channelsJoined = false;
      }

    },

    //joins the tenant channels for listening for complete calls
    async joinTenantChannels() {

      if(!this.tenantChannelJoined){
        try {
          await feathersClient.service('channel-memberships').create({
            roomType: 'complete-calls',
            tenantId: this.selectedTenant.id
          }).then(res => console.log('tenant channel', res));
          this.tenantChannelJoined = true;
        } catch (e) {
          console.log('could not join tenant channel', e);
          this.tenantChannelJoined = false;
        }
      }

    },

    //leaves the currently joined real time channels
    async leaveQueueChannels() {

      const requests = this.queueIds.map(queueId => {
        return feathersClient.service('channel-memberships').remove(null, {
          query: {
            channelName: `queues/${queueId}`,
          }
        }).then(res => console.log(res));
      });

      try {
        await Promise.all(requests);
        this.channelsJoined = false;
      } catch (e) {
        console.log('could not leave channels', e);
        this.channelsJoined = true;
      }
    },

    //leave the tenant channel for complete calls
    async leaveTenantChannels() {

      try {
        await feathersClient.service('channel-memberships').remove(null, {
          query: {
            channelName: `complete-calls/${this.selectedTenant.id}`
          }
        }).then(res => console.log('tenant channel', res));
        this.tenantChannelJoined = false;
      } catch (e) {
        console.log('could not leave tenant channel', e);
        this.tenantChannelJoined = true;
      }

    },

    checkPastMidnight() {
      const now = moment().format('YYYY-MM-DD');
      if(now > this.pageLoadTime && !this.statResetComplete) {
        this.pageLoadTime = now;
        this.statResetComplete = true;
        this.loadDashboard(this.selectedQueueLayout);
      } else if(now === this.pageLoadTime && this.statResetComplete) {
        this.statResetComplete = false;
      }
    },

    //remove all the event listeners when this lifecycle is no longer active
    //nice way to reference all the events we are listening for here
    removeEventListeners() {

      //global app event listeners
      globalEventBus.$off('appReconnected');
      globalEventBus.$off('queueManagerDashboardEvent');

      //stat related real time events
      const channelService = feathersClient.service('channel-memberships');
      channelService.removeListener('agentComplete');
      channelService.removeListener('queueAbandon');
      channelService.removeListener('queueExitTimeout');
      channelService.removeListener('queueExitKey');
      channelService.removeListener('queueAttendedTransfer');
      channelService.removeListener('queueBlindTransfer');
      channelService.removeListener('queueEntry');
      channelService.removeListener('queueRefresh');
      channelService.removeListener('callbackRequested');
      channelService.removeListener('callbackComplete');
      channelService.removeListener('agentRefused');
      channelService.removeListener('queueMemberPause');
      channelService.removeListener('queueCallerJoin');
      channelService.removeListener('queueCallerConnected');
      channelService.removeListener('queueCallerLeave');
      channelService.removeListener('callComplete');
    },

    cleanupStore() {
      this.$store.commit('queueDashQueues/clearList');
      this.$store.commit('queueDashAgents/clearList');
      this.$store.commit('queueDashWaiting/clearList');
      this.$store.commit('queueDashActive/clearList');
      this.$store.commit('queueDashAgentAggregates/clearList');
      this.$store.commit('setQueueManagerAggregates', {});
    }

  },

  created() {

    //reconnect logic to reinitialize the dashboard
    globalEventBus.$on('appReconnected',  () => {
      console.log('the app did reconnect');
      this.channelsJoined = false;
      this.tenantChannelJoined = false;
      this.initDashboard();
    });

    //toolbar interactions that trigger router view changes
    globalEventBus.$on('queueManagerDashboardEvent', data => {

      const { event } = data;

      //console.log(event);

      switch (event) {
        case 'loadDashboard':
          this.loadDashboard(this.selectedQueueLayout);
          break;
        case 'loadEditLayoutForm':
          this.loadEditLayoutForm();
          break;
        case 'removeLayout':
          this.removeLayout();
          break;
        case 'loadCreateLayoutForm':
          this.loadCreateLayoutForm();
          break;
        case 'duplicateLayout':
          this.duplicateLayout();
          break;
        default:
          this.widgetBuilder(event);
          break;
      }

    })

    //event listeners and store mutation handlers
    const channelService = feathersClient.service('channel-memberships');

    //completed queue call
    channelService.on('agentComplete', item => {

      console.log('queue complete', item);

      const { waitTime, queueId, peerName, duration, channel } = item;

      this.$store.commit('queueDashActive/removeItem', channel)

      //update the queue in the store
      let queue = {...this.queueStoreItem(queueId)};
      if(queue.activeCalls > 0) queue.activeCalls--;
      queue.totalInbound++;
      queue.inboundComplete++
      queue.waitTime += +waitTime;
      queue.waitTimeAnswered += +waitTime;
      queue.avgTimeToAnswer = +(queue.waitTimeAnswered / queue.inboundComplete).toFixed(0);
      queue.abandonRate = +(queue.abandoned / queue.totalInbound * 100).toFixed(0);
      this.$store.commit('queueDashQueues/updateItem', queue)

      //update the agent in the store for this particular queue
      const searchAgent = this.agentStoreList({
        query: {
          queueId: +queueId,
          peerName
        }
      }).data;

      if(searchAgent.length){
        const agent = {...searchAgent[0]};
        const agentAggregate = {...this.aggregateAgentItem(peerName)};
        agent.inboundComplete++;
        agent.inboundTalkTime += +duration;
        agent.waitTimeAnswered += +waitTime;

        agentAggregate.inboundComplete++;
        agentAggregate.inboundTalkTime += +duration;
        agentAggregate.waitTimeAnswered += +waitTime;

        this.$store.commit('queueDashAgents/updateItem', agent);
        this.$store.commit('queueDashAgentAggregates/updateItem', agentAggregate);
      }

    });

    //abandoned queue call
    channelService.on('queueAbandon', item => {
     // console.log('queue abandon', item);
      const { queueId, holdTime } = item;
      let queue = {...this.queueStoreItem(queueId)};
      queue.totalInbound++;
      queue.abandoned++;
      queue.waitTime += +holdTime;
      queue.waitTimeAbandoned += +holdTime;
      queue.avgAbandonWaitTime = +(queue.waitTimeAbandoned / queue.abandoned).toFixed(0);
      queue.abandonRate = +(queue.abandoned / queue.totalInbound * 100).toFixed(0);
      this.$store.commit('queueDashQueues/updateItem', queue);
    });

    //exit timeout events
    channelService.on('queueExitTimeout', item => {
     // console.log('queue exit timeout', item);
      const { queueId, holdTime } = item;
      let queue = {...this.queueStoreItem(queueId)};
      queue.totalInbound++;
      queue.totalExits++;
      queue.exitTimeout++;
      queue.waitTime += +holdTime;
      queue.abandonRate = +(queue.abandoned / queue.totalInbound * 100).toFixed(0);
      this.$store.commit('queueDashQueues/updateItem', queue);
    });

    //exit key events
    channelService.on('queueExitKey', item => {
     // console.log('queue exit key', item);
      const { queueId, holdTime } = item;
      let queue = {...this.queueStoreItem(queueId)};
      queue.totalInbound++;
      queue.totalExits++;
      queue.exitKey++;
      queue.waitTime += +holdTime;
      queue.abandonRate = +(queue.abandoned / queue.totalInbound * 100).toFixed(0);
      this.$store.commit('queueDashQueues/updateItem', queue);
    });

    //attended transfer event
    channelService.on('queueAttendedTransfer', item => {
     console.log('queue attended transfer', item);
      const { queueId, agent, channel } = item;

      this.$store.commit('queueDashActive/removeItem', channel);

      let queue = {...this.queueStoreItem(queueId)};
      // if(queue.activeCalls > 0) queue.activeCalls--;
      queue.totalTransfers++;
      queue.attendedTransfers++;
      this.$store.commit('queueDashQueues/updateItem', queue);

      const searchAgent = this.agentStoreList({
        query: {
          queueId: +queueId,
          name: agent
        }
      }).data;

      if(searchAgent.length){
        const agent = {...searchAgent[0]};
        const aggregateAgent = {...this.aggregateAgentItem(agent.peerName)};
        agent.totalTransfers++;
        agent.attendedTransfers++;
        aggregateAgent.totalTransfers++;
        aggregateAgent.attendedTransfers++;

        this.$store.commit('queueDashAgents/updateItem', agent);
        this.$store.commit('queueDashAgentAggregates/updateItem', aggregateAgent);
      }

    });

    //blind transfer event
    channelService.on('queueBlindTransfer', item => {
     console.log('queue blind transfer', item);
      const { queueId, agent, channel } = item;

      this.$store.commit('queueDashActive/removeItem', channel);

      let queue = {...this.queueStoreItem(queueId)};
      // if(queue.activeCalls > 0) queue.activeCalls--;
      queue.totalTransfers++;
      queue.blindTransfers++;
      this.$store.commit('queueDashQueues/updateItem', queue);

      const searchAgent = this.agentStoreList({
        query: {
          queueId: +queueId,
          name: agent
        }
      }).data;

      if(searchAgent.length){
        const agent = {...searchAgent[0]};
        const aggregateAgent = {...this.aggregateAgentItem(agent.peerName)};
        agent.totalTransfers++;
        agent.blindTransfers++;
        aggregateAgent.totalTransfers++;
        aggregateAgent.blindTransfers++;
        this.$store.commit('queueDashAgents/updateItem', agent);
        this.$store.commit('queueDashAgentAggregates/updateItem', aggregateAgent);
      }

    });

    //queue callback requested
    channelService.on('callbackRequested', item => {
     // console.log('callback requested', item);
      const { queueId } = item;
      let queue = {...this.queueStoreItem(queueId)};
      queue.queuedCallbacks++;
      this.$store.commit('queueDashQueues/updateItem', queue);
    });

    //queue callback complete
    channelService.on('callbackComplete', item => {
     // console.log('callback complete', item);
      const { queueId, success } = item;
      let queue = {...this.queueStoreItem(queueId)};
      if(queue.queuedCallbacks > 0) queue.queuedCallbacks--;
      queue.completedCallbacks++;
      success ? queue.successfulCallbacks++ : queue.failedCallbacks++
      this.$store.commit('queueDashQueues/updateItem', queue);
    });

    //agent refuse event
    channelService.on('agentRefused', item => {
    //  console.log('refuse happened', item);
      const { queueId, peerName } = item;

      let queue = {...this.queueStoreItem(queueId)};
      queue.refused++;
      this.$store.commit('queueDashQueues/updateItem', queue);

      const searchAgent = this.agentStoreList({
        query: {
          queueId: +queueId,
          peerName
        }
      }).data;

      if(searchAgent.length){
        const agent = {...searchAgent[0]};
        const aggregateAgent = {...this.aggregateAgentItem(peerName)};
        agent.refused++;
        aggregateAgent.refused++;
        this.$store.commit('queueDashAgents/updateItem', agent);
        this.$store.commit('queueDashAgentAggregates/updateItem', aggregateAgent);
      }
    });

    //queue member paused event
    channelService.on('queueMemberPause', item => {
     // console.log('got a pause event', item);

      const {peerName, paused, reason, queueId} = item;

      const searchAgent = this.agentStoreList({
        query: {
          queueId: +queueId,
          peerName
        }
      }).data;

      if(searchAgent.length) {
        const agent = {...searchAgent[0]};
        agent.paused = paused;
        agent.lastPauseReason = reason;
        agent.lastPauseUnix = moment().unix();
        this.$store.commit('queueDashAgents/updateItem', agent);
      }

    });

    //login status for agent
    channelService.on('queueRefresh', item => {

    //  console.log('got a refresh event', item);

      if(!item.hasOwnProperty('loggedin')) return;

      const { peer, loggedin, queueId } = item;
      const searchAgent = this.agentStoreList({
        query: {
          peerName: peer,
          queueId: +queueId
        }
      }).data;

    //  console.log('agent is', searchAgent);

      if(searchAgent.length) {
        const agent = {...searchAgent[0]};
        agent.loggedIn = +loggedin;
        this.$store.commit('queueDashAgents/updateItem', agent);
      }

      //see if the agent is logged in anywhere ?
      const loggedInSearch = this.agentStoreList({
        query: {
          peerName: peer,
          loggedIn: 1
        }
      }).data;

      this.$store.commit('queueDashAgentAggregates/updateItem', {peerName: peer, loggedIn: loggedInSearch.length ? 1 : 0});

    });

    //a new caller has joined the queue line
    channelService.on('queueCallerJoin', item => {

    // console.log('got a caller join event', item);

      const { queueId, calleridname: callerName, calleridnum: callerNum } = item;
      let queue = {...this.queueStoreItem(queueId)};
      queue.waitingCalls++;
      this.$store.commit('queueDashQueues/updateItem', queue);

      const callItem = {
        callerNum,
        callerName,
        holdTime: 0,
        ...item
      }
      this.$store.commit('queueDashWaiting/addItem', callItem);
    });

    //a caller connected to an agent in the queue and left the line
    channelService.on('queueCallerConnected', item => {

     console.log('got a caller connected event', item);

      const { queueId } = item;

      let queue = {...this.queueStoreItem(queueId)};
      queue.activeCalls++;
      this.$store.commit('queueDashActive/addItem', {...item, talkTime: 0});
      this.$store.commit('queueDashQueues/updateItem', queue);
    });

    //a caller left the queue line
    channelService.on('queueCallerLeave', item => {

    // console.log('got a caller leave event', item);

      const { queueId, channel } = item;

      let queue = {...this.queueStoreItem(queueId)};
      if(queue.waitingCalls > 0) queue.waitingCalls--;
      this.$store.commit('queueDashQueues/updateItem', queue);
      this.$store.commit('queueDashWaiting/removeItem', channel);
    });

    //a call was completed for this tenant
    channelService.on('callComplete', item => {
    //  console.log('complete call', item);

      const { callerIdNum, whoAnswered, disposition, direction } = item;
      const { tenantCode } = this.selectedTenant;
      const peerName = direction === 'OUT' ? `${callerIdNum}-${tenantCode}` : `${whoAnswered}-${tenantCode}`;

      const searchAgent = this.agentStoreList({
        query: {
          peerName
        }
      }).data;



      //if we find the peer we need increment queues and agent
      if(searchAgent.length){

        //aggregates first
        let currentAggregate = {...this.aggregateAgentItem(peerName)};
        if(direction === 'OUT'){
          currentAggregate.totalOutbound++;
          disposition === 'ANSWERED' ? currentAggregate.outboundAnswered++ : currentAggregate.outboundNoAnswer++;
        }

        if(direction === 'IN'){
          currentAggregate.inboundDirect++
        }

        this.$store.commit('queueDashAgentAggregates/updateItem', currentAggregate);

        //per queue updates
        for(let agent of searchAgent) {
          let currentAgent = {...agent};

          if(direction === 'OUT'){
            currentAgent.totalOutbound++;
            disposition === 'ANSWERED' ? currentAgent.outboundAnswered++ : currentAgent.outboundNoAnswer++;
          }

          if(direction === 'IN'){
            currentAgent.inboundDirect++
          }

          this.$store.commit('queueDashAgents/updateItem', currentAgent);

        }

        if(direction === 'OUT') {
          const queueIds = searchAgent.map(item => item.queueId);
          for(let queueId of queueIds){
            let queue = {...this.queueStoreItem(queueId)};
            queue.totalOutbound++;
            disposition === 'ANSWERED' ? queue.outboundAnswered++ : queue.outboundNoAnswer++;
            this.$store.commit('queueDashQueues/updateItem', queue);
          }
        }
      }
    });

  },

  mounted() {
    this.initDashboard();
    this.resetPoller = setInterval(this.checkPastMidnight, 1000 * 60);
  },

  beforeDestroy() {
    clearInterval(this.resetPoller);
    clearInterval(this.durationPoller);
    clearInterval(this.pausePoller);
    this.removeEventListeners();
    this.leaveQueueChannels();
    this.leaveTenantChannels();
    this.cleanupStore();
  }
}

</script>
